import { useCallback, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Container, Row, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { AsyncPage } from '@/components';
import { inquiryService } from '@/lib/services';
import { InquiryDocument } from '@/lib/models';
import { useAnalytics, useQuery, useNavigationConfig, createUseThemedStyles } from '@/hooks';
import { constructUrl } from '@/lib/utils';
import { Download } from 'react-bootstrap-icons';

const useStyles = createUseThemedStyles((_theme) => ({
	documentFrameLarge: {
		width: '100%',
		height: '80vh',
		border: '1px solid #DEE2E6',
	},
	documentFrameMedium: {
		width: '100%',
		minHeight: '60vh',
		border: '1px solid #DEE2E6',
	},
	documentImage: {
		maxWidth: '100%',
		height: 'auto',
	},
}));

export const InquiryDocumentDetail = () => {
	const query = useQuery();
	const navigate = useNavigate();
	const analytics = useAnalytics();
	const { navigationConfig } = useNavigationConfig();
	const classes = useStyles();
	const { inquiryId, documentId } = useParams<{ inquiryId?: string; documentId?: string }>();
	const [document, setDocument] = useState<InquiryDocument>();
	const aiConversationIdNav = useMemo(
		() => query.get('aiConversationIdNav') ?? query.get('aiConversationId'),
		[query]
	);

	const fetchData = useCallback(async () => {
		if (!inquiryId) {
			throw new Error('inquiryId is undefined');
		}

		if (!documentId) {
			throw new Error('documentId is undefined');
		}

		const { inquiryDocument } = await inquiryService.getInquiryDocument(inquiryId, documentId).fetch();
		setDocument(inquiryDocument);
	}, [documentId, inquiryId]);

	const embedType = useMemo(() => {
		if (!document) return undefined;
		const name = (document.name || '').toLowerCase();
		if (name.endsWith('.pdf')) return 'pdf';
		if (name.endsWith('.txt') || name.endsWith('.html')) return 'iframe';
		return undefined;
	}, [document]);

	const fileExtension = useMemo(() => {
		if (!document?.name) return undefined;
		const match = document.name.toLowerCase().match(/\.([^.]+)$/);
		return match ? match[1] : undefined;
	}, [document]);

	const handleDownloadClick = useCallback(async () => {
		const { downloadUrl } = document ?? {};
		if (downloadUrl) {
			try {
				if (!inquiryId) {
					throw new Error('inquiryId is undefined');
				}

				if (!documentId) {
					throw new Error('documentId is undefined');
				}

				await inquiryService.trackInquiryDocumentDownload(inquiryId, documentId).fetch();
				analytics.handleClickEvent(downloadUrl);
			} catch (error) {
				// fail silently because we dont need to report a failed analytics call
			} finally {
				window.open(downloadUrl, '_blank', 'noopener');
			}
		}
	}, [document, inquiryId, documentId, analytics]);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Inquiries - Document Details</title>
			</Helmet>
			<Container className="py-6">
				{query.get('ref') === 'chat' && (
					<Row className="mb-2">
						<Col>
							<p className="mb-0 text-muted">
								<Button
									className="mr-4 mb-0 p-0"
									variant="link"
									onClick={() => {
										navigate(
											constructUrl('/ask-hackett', {
												...(aiConversationIdNav && {
													aiConversationId: aiConversationIdNav,
												}),
											})
										);
									}}
								>
									AskHackett&trade;
								</Button>
							</p>
						</Col>
					</Row>
				)}
				<Row className="mb-5">
					<Col>
						<h5 className="mb-3 font-weight-regular text-muted">Document</h5>
						<h1 className="mb-3">{document?.name || 'Document'}</h1>
						<h5 className="mb-6 font-weight-regular text-muted">{document?.createdDescription}</h5>
						<hr />
					</Col>
				</Row>
				<Row>
					<Col xs={12} xl={8} className="mb-6">
						{!document && <p className="text-muted">Document not found for this inquiry.</p>}
						{document && (
							<div>
								{embedType && document.viewUrl && (
									<div className="mb-5">
										{embedType === 'pdf' && (
											<div className="d-flex justify-content-center">
												<iframe
													title={document.name}
													src={document.viewUrl}
													className={classes.documentFrameLarge}
												/>
											</div>
										)}
										{embedType === 'iframe' && (
											<div className="d-flex justify-content-center">
												<iframe
													title={document.name}
													src={document.viewUrl}
													className={classes.documentFrameMedium}
												/>
											</div>
										)}
									</div>
								)}

								{document && !embedType && (
									<div className="mb-5">
										<p className="mb-3 font-weight-bold">
											{`This document is ${
												fileExtension
													? `a ${fileExtension.toUpperCase()} file.`
													: 'an unknown file type.'
											} Download the document to view the full version.`}
										</p>
									</div>
								)}
							</div>
						)}
					</Col>
					<Col xs={12} xl={4}>
						{navigationConfig?.allowDownloads && document?.downloadUrl && (
							<Button
								className="d-flex align-items-center w-100"
								variant="outline-primary"
								onClick={handleDownloadClick}
							>
								<Download className="mr-2" width={20} height={20} />
								Download Document
							</Button>
						)}
					</Col>
				</Row>
			</Container>
		</AsyncPage>
	);
};

export default InquiryDocumentDetail;
